import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import * as _ser from "../../../_redux/_services/ProjectsSlice";
import CkEditor from "../../../components/CkEditor/CkEditor";
import "./projects_create.css";
import { GetUserId, getAttributes } from "../../../_utilities/getUserInfo";
import { updateSnackbar } from "../../../_redux/SnackbarSlice";
import { defaultApplicantsData } from "../../../_utilities/defaultfieldsModel";
const initialDetails = {
  name: "",
  createdBy: "",
  status: 1,
  selectedStatuses: [],
  internalVisibility: "",
  allowedMembers: [],
  externalAccess: "",
  authorizedExternalUsers: [],
  hideContactInfo: false,
  hideDocuments: true,
  isNotesVisible: false,
  isApplicantStatusEnabled: false,
  description: "",
  userId: "",
};

const initProjects = JSON.parse(JSON.stringify(initialDetails));

const nameRegex = /^[A-Za-z]+([ '-][A-Za-z]+)*$/;

const externalUserSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .matches(nameRegex, "Enter a valid first name")
    .required("First name is required"),
  lastName: Yup.string()
    .trim()
    .matches(nameRegex, "Enter a valid last name")
    .required("Last name is required"),
  emailId: Yup.string()
    .trim()
    .email("Invalid email format")
    .required("Email ID is required"),
});

const ProjectsCreate = ({ onReceiveProps }) => {
  const [projects, setProjects] = useState(
    JSON.parse(JSON.stringify(initProjects)),
  );
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);
  const [projectDescriptionValue, setProjectDescriptionValue] = useState("");
  const [isDescriptionError, setIsDescriptionError] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [candidateStatusValues, setCandidateStatusValues] = useState([]);

  const customFieldsData = useSelector((state) => state.CustomFields?.data);

  const [extUser, setExtUser] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
  });
  const [extUserErrors, setExtUserErrors] = useState({});
  const [editingExtUserIndex, setEditingExtUserIndex] = useState(null);

  const dispatch = useDispatch();

  const AccessibleTeams = useSelector((state) => state.GetAccessibleTo.data);

  useEffect(() => {
    console.log("projects", projects);
  }, [projects]);

  useEffect(() => {
    const init = async () => {
      const userId = await GetUserId();
      dispatch(_ser.getAccessibleToAsync(userId));
    };
    init();
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(1, "Must be 1 characters")
      .max(50, "Must be less than 50 ")
      .required("Please enter a project's name"),
    internalVisibility: Yup.string().required(
      "Please select internal visibility",
    ),
    allowedMembers: Yup.array().when("internalVisibility", {
      is: (val) => val === "restricted",
      then: (schema) =>
        schema.min(1, "Allowed Members is required for restricted visibility"),
      otherwise: (schema) => schema.notRequired(),
    }),
    externalAccess: Yup.string().required("Please select external access"),
    description: Yup.string().required(
      "Description is required. Please provide a description",
    ),
    selectedStatuses: Yup.array()
      .min(1, "Please select at least one applicant status")
      .required("Please select at least one applicant status"),
  });

  const formik = useFormik({
    initialValues: JSON.parse(JSON.stringify(initProjects)),
    validationSchema,
    validate: async (values) => {
      const desc = values.description || projectDescriptionValue;
      setIsDescriptionError(isEmptyDescription(desc));
    },
    onSubmit: async (values) => {
      if (!selectedStatuses || selectedStatuses.length === 0) {
        formik.setFieldError(
          "selectedStatuses",
          "Please select at least one applicant status",
        );
        formik.setFieldTouched("selectedStatuses", true, false);
        dispatch(
          updateSnackbar({
            type: "error",
            message: "Please select at least one applicant status.",
          }),
        );
        setDisableSubmitBtn(false);
        return;
      }

      let currentUsersLocal = [...projects.authorizedExternalUsers];

      // If the user has typed a valid entry but hasn't clicked "Add Row", add it so validation passes
      if (projects.externalAccess === "restricted-link-access") {
        if (extUser.firstName || extUser.lastName || extUser.emailId) {
          try {
            await externalUserSchema.validate(extUser, { abortEarly: false });
            currentUsersLocal.push(extUser);
            onChangeHandler(currentUsersLocal, "authorizedExternalUsers");
            setExtUser({ firstName: "", lastName: "", emailId: "" });
            setExtUserErrors({});
            setEditingExtUserIndex(null);
          } catch (err) {
            if (err.inner) {
              const errors = {};
              err.inner.forEach((validationError) => {
                errors[validationError.path] = validationError.message;
              });
              setExtUserErrors(errors);
              setDisableSubmitBtn(false);
              return; // Stop the save process because of the invalid draft
            }
          }
        }
      }

      if (
        projects.externalAccess === "restricted-link-access" &&
        (!currentUsersLocal || currentUsersLocal.length === 0)
      ) {
        dispatch(
          updateSnackbar({
            type: "error",
            message:
              "Add at least one authorized external user to enable Restricted Link Access.",
          }),
        );
        return;
      }

      const attributes = await getAttributes();
      let userString = `${attributes[0].id}@${attributes[0]?.firstName} ${attributes[0]?.lastName}`;

      // Construct payload according to specific API requirements
      const payload = {
        name: values.name,
        createdBy: "",
        visibility:
          projects.internalVisibility === "organization-wide"
            ? "public"
            : projects.internalVisibility,
        accessibleTo: JSON.stringify(
          projects.internalVisibility === "restricted"
            ? projects.allowedMembers
            : [userString],
        ),
        description: projectDescriptionValue,
        status: 1,
        applicantStatus:
          candidateStatusValues.length > 0 &&
          selectedStatuses.length === candidateStatusValues.length
            ? " "
            : selectedStatuses.join(","),
        userId: attributes ? attributes[0]?.userId : "",
        hideContactInfo: projects.hideContactInfo ? "1" : "0",
        hideDocuments:
          projects.hideContactInfo && projects.hideDocuments ? "1" : "0",
        isNotesVisible: projects.isNotesVisible ? 1 : 0,
        isApplicantStatusEnabled: projects.isApplicantStatusEnabled ? 1 : 0,
        externalAccessType:
          projects.externalAccess === "restricted-link-access"
            ? "RESTRICTED"
            : projects.externalAccess === "open-link-access"
              ? "OPEN"
              : "DISABLED",
        externalPortalUsers:
          projects.externalAccess === "restricted-link-access"
            ? currentUsersLocal
            : projects.authorizedExternalUsers || [],
      };

      setDisableSubmitBtn(true);
      await CreateProjects(payload);
    },
  });

  const CreateProjects = async (values) => {
    try {
      await dispatch(_ser.addProjectsAsync(values));
    } catch (error) {
      console.log(error);
    } finally {
      setDisableSubmitBtn(false);
    }
  };

  const onChangeHandler = (value, type) => {
    let obj = JSON.parse(JSON.stringify(projects));
    if (type === "internalVisibility" && value !== "restricted") {
      obj.allowedMembers = [];
      formik.values.allowedMembers = [];
      obj.isNotesVisible = false;
      obj.isApplicantStatusEnabled = false;
      formik.values.isNotesVisible = false;
      formik.values.isApplicantStatusEnabled = false;
    }
    if (type === "externalAccess") {
      if (value !== "restricted-link-access") {
        obj.authorizedExternalUsers = [];
        formik.values.authorizedExternalUsers = [];
      }
      if (value !== "restricted-link-access" && value !== "open-link-access") {
        obj.hideContactInfo = false;
        formik.values.hideContactInfo = false;
      }
      obj.isNotesVisible = false;
      obj.isApplicantStatusEnabled = false;
      formik.values.isNotesVisible = false;
      formik.values.isApplicantStatusEnabled = false;
    }
    obj[type] = value;
    formik.values[type] = value;
    setProjects(obj);
  };

  const handleAddExternalUser = async () => {
    try {
      // Clear previous errors
      setExtUserErrors({});

      // Validate using Yup schema
      await externalUserSchema.validate(extUser, { abortEarly: false });

      // If validation passes, add/update the user
      const updatedUsers = [...projects.authorizedExternalUsers];

      if (editingExtUserIndex !== null) {
        updatedUsers[editingExtUserIndex] = extUser;
        setEditingExtUserIndex(null);
      } else {
        updatedUsers.push(extUser);
      }

      onChangeHandler(updatedUsers, "authorizedExternalUsers");
      handleClearExternalUserInput();
    } catch (err) {
      if (err.inner) {
        const errors = {};
        err.inner.forEach((validationError) => {
          errors[validationError.path] = validationError.message;
        });
        setExtUserErrors(errors);
      }
    }
  };

  const handleClearExternalUserInput = () => {
    setExtUser({ firstName: "", lastName: "", emailId: "" });
    setExtUserErrors({});
    setEditingExtUserIndex(null);
  };

  // const handleEditExternalUser = (index) => {
  //   const userToEdit = projects.authorizedExternalUsers[index];
  //   setExtUser({ ...userToEdit });
  //   setEditingExtUserIndex(index);
  // };

  const handleRemoveExternalUser = (index) => {
    const updatedUsers = projects.authorizedExternalUsers.filter(
      (_, i) => i !== index,
    );
    onChangeHandler(updatedUsers, "authorizedExternalUsers");
    if (editingExtUserIndex === index) {
      handleClearExternalUserInput();
    }
  };

  useEffect(() => {
    let defaultPostingModules = defaultApplicantsData.filter(
      (x) => x.moduleId === "2",
    );
    if (defaultPostingModules.length > 0) {
      defaultPostingModules.forEach((list) => {
        if (list.fieldName === "Candidate status") {
          setCandidateStatusValues(list.customValues || []);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (customFieldsData && customFieldsData.length > 0) {
      const data = JSON.parse(JSON.stringify(customFieldsData));
      let customStatusFieldsData = data.filter(
        (x) =>
          x.moduleId.toString() === "2" &&
          x.fieldType === "default" &&
          x.fieldName === "Candidate status",
      );
      if (customStatusFieldsData.length > 0) {
        let index = customStatusFieldsData.length - 1;
        let val = customStatusFieldsData[index].customValues;
        let array = typeof val === "string" ? JSON.parse(val) : val;
        setCandidateStatusValues(array);
      }
    }
  }, [customFieldsData]);

  const isEmptyDescription = (value) => {
    if (!value) return true;
    const text = value
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return text.length === 0;
  };

  const onDesciptionHandler = (value) => {
    const empty = isEmptyDescription(value);
    formik.setFieldValue("description", empty ? "" : value, true);
    setProjectDescriptionValue(value);
    setIsDescriptionError(empty);
  };

  // Map stored string values like "id@Full Name" to option objects for Autocomplete value
  const selectedAllowedMembersOptions =
    AccessibleTeams?.filter((item) =>
      projects.allowedMembers?.includes(item.id + "@" + item.fullName),
    ) || [];

  return (
    <div className="projectsCreateContainer">
      <FocusError formik={formik} />
      <TextField
        className="boldlabel"
        fullWidth
        error={formik.errors.name && formik.touched.name ? true : false}
        helperText={
          formik.errors.name && formik.touched.name ? formik.errors.name : ""
        }
        autoFocus={formik.errors.name !== "" ? true : false}
        id="outlined-basic"
        variant="outlined"
        autoComplete="off"
        label="Project Name*&nbsp;"
        style={{ marginBottom: "40px" }}
        name="name"
        value={projects.name ? projects.name : ""}
        InputLabelProps={{
          shrink: true,
        }}
        onChange={(e) => onChangeHandler(e.target.value, "name")}
        onKeyUp={formik.handleBlur}
      />
      <FormControl
        fullWidth
        error={
          formik.errors.description &&
            formik.touched.description &&
            isDescriptionError === true
            ? true
            : false
        }
      >
        <InputLabel
          shrink={true}
          style={{
            marginTop: "-10px",
            marginLeft: "-13px",
            background: "none",
          }}
          className={
            formik.errors.description &&
              formik.touched.description &&
              isDescriptionError
              ? "text-danger boldSelectlabel"
              : "boldSelectlabel"
          }
        >
          Project Description*
        </InputLabel>
        <div
          id="projectDesc"
          className={
            formik.errors.description &&
              formik.touched.description &&
              isDescriptionError
              ? "brd-danger"
              : ""
          }
        >
          <CkEditor
            data={
              projectDescriptionValue && projectDescriptionValue === ""
                ? " "
                : projectDescriptionValue
            }
            error={
              formik.errors.description &&
                formik.touched.description &&
                isDescriptionError
                ? true
                : false
            }
            name="description"
            autoComplete="off"
            isReadonly={false}
            onUpdatehandler={(update) => {
              onDesciptionHandler(update.data);
            }}
          />
        </div>
        {formik.errors.description &&
          formik.touched.description &&
          isDescriptionError ? (
          <FormHelperText className="text-danger">
            Description is required. Please provide a description
          </FormHelperText>
        ) : (
          ""
        )}
      </FormControl>
      <div className="statusDropdownContainer">
        <div
          className="statusMultipleSelectContainer"
          style={{ marginTop: "15px" }}
        >
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(
              formik.errors.selectedStatuses && formik.touched.selectedStatuses,
            )}
          >
            <InputLabel className="boldSelectlabel" shrink={true}>
              Selected Applicant Status*&nbsp;
            </InputLabel>
            <Select
              className="statusSelectDropdown"
              multiple
              label="Selected Applicant Status &nbsp;"
              displayEmpty
              value={selectedStatuses}
              onChange={(e) => {
                const value = e.target.value;
                const selectedArray =
                  typeof value === "string" ? value.split(",") : value;
                let finalSelected = [];
                if (selectedArray.includes("all")) {
                  if (
                    candidateStatusValues.length > 0 &&
                    selectedStatuses.length === candidateStatusValues.length
                  ) {
                    finalSelected = [];
                  } else {
                    finalSelected = candidateStatusValues.map((item) =>
                      String(item.internal || item)
                    );
                  }
                } else {
                  finalSelected = selectedArray.map(String);
                }
                setSelectedStatuses(finalSelected);
                formik.setFieldValue("selectedStatuses", finalSelected, true);
                formik.setFieldTouched("selectedStatuses", true, false);
              }}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return "";
                }
                return candidateStatusValues
                  .filter((item) => selected.includes(String(item.internal || item)))
                  .map((item) => item.internal || item)
                  .join(", ");
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: "calc(100vh - 450px)",
                  },
                },
                sx: {
                  "& .MuiMenuItem-root": {
                    display: "flex",
                    justifyContent: "flex-start",
                    width: "100%",
                    padding: "8px 16px",
                  },
                },
              }}
            >
              {candidateStatusValues.length > 0 && (
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      candidateStatusValues.length > 0 &&
                      selectedStatuses.length === candidateStatusValues.length
                    }
                    indeterminate={
                      selectedStatuses.length > 0 &&
                      selectedStatuses.length < candidateStatusValues.length
                    }
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
              )}
              {candidateStatusValues.map((statusItem, idx) => {
                const statusLabel = statusItem.internal || statusItem;
                return (
                  <MenuItem key={idx} value={statusLabel}>
                    <Checkbox
                      checked={selectedStatuses.includes(statusLabel)}
                    />
                    <ListItemText primary={statusLabel} />
                  </MenuItem>
                );
              })}
            </Select>
            {formik.errors.selectedStatuses &&
              formik.touched.selectedStatuses && (
                <FormHelperText className="text-danger">
                  {formik.errors.selectedStatuses}
                </FormHelperText>
              )}
          </FormControl>
        </div>
      </div>

      <FormControl
        fullWidth
        error={
          formik.errors.internalVisibility && formik.touched.internalVisibility
            ? true
            : false
        }
        style={{ marginBottom: "40px", marginTop: "30px" }}
      >
        <InputLabel className="boldSelectlabel" shrink={true}>
          Internal Visibility*&nbsp;
        </InputLabel>
        <Select
          value={projects.internalVisibility ? projects.internalVisibility : ""}
          name="internalVisibility"
          fullWidth
          label="Internal Visibility &nbsp;"
          onBlur={formik.handleBlur}
          onChange={(e) =>
            onChangeHandler(e.target.value, "internalVisibility")
          }
          autoComplete="off"
          style={{ height: "3.5rem", borderRadius: "4px" }}
        >
          {internalVisibilityArray &&
            internalVisibilityArray.map((list) => {
              return (
                <MenuItem
                  className="selectOptions"
                  key={list.id}
                  value={list.name}
                >
                  {list.title}
                </MenuItem>
              );
            })}
        </Select>
        <FormHelperText>
          {formik.errors.internalVisibility && formik.touched.internalVisibility
            ? formik.errors.internalVisibility
            : ""}
        </FormHelperText>
      </FormControl>

      {projects.internalVisibility === "restricted" && (
        <div style={{ width: "100%", marginBottom: "40px" }}>
          <FormControl
            sx={{ width: "100%" }}
            fullWidth
            error={
              formik.errors.allowedMembers && formik.touched.allowedMembers
                ? true
                : false
            }
          >
            <Tooltip
              title="Only selected internal members will have access to this project"
              placement="top-start"
              arrow
            >
              <InputLabel
                className="boldSelectlabel"
                id="demo-multiple-chip-label"
                shrink={true}
              >
                Allowed Members*&nbsp;
              </InputLabel>
            </Tooltip>
            <Autocomplete
              multiple
              options={AccessibleTeams || []}
              getOptionLabel={(option) => option.fullName || ""}
              value={selectedAllowedMembersOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, value) => {
                const vals = value.map((item) => item.id + "@" + item.fullName);
                onChangeHandler(vals, "allowedMembers");
              }}
              onBlur={() => formik.setFieldTouched("allowedMembers", true)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    style={{ backgroundColor: "#ebebeb", borderRadius: "30px" }}
                    label={option.fullName}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={
                    formik.errors.allowedMembers &&
                      formik.touched.allowedMembers
                      ? true
                      : false
                  }
                />
              )}
            />
            <FormHelperText>
              {formik.errors.allowedMembers && formik.touched.allowedMembers
                ? formik.errors.allowedMembers
                : ""}
            </FormHelperText>
          </FormControl>
        </div>
      )}

      <FormControl
        fullWidth
        error={
          formik.errors.externalAccess && formik.touched.externalAccess
            ? true
            : false
        }
        style={{ marginBottom: "25px" }}
      >
        <InputLabel className="boldSelectlabel" shrink={true}>
          External Access*&nbsp;
        </InputLabel>
        <Select
          value={projects.externalAccess ? projects.externalAccess : ""}
          name="externalAccess"
          fullWidth
          label="External Access &nbsp;"
          onBlur={formik.handleBlur}
          onChange={(e) => onChangeHandler(e.target.value, "externalAccess")}
          autoComplete="off"
          style={{ height: "3.5rem", borderRadius: "4px" }}
        >
          {externalAccessArray &&
            externalAccessArray.map((list) => {
              return (
                <MenuItem
                  className="selectOptions"
                  key={list.id}
                  value={list.name}
                >
                  {list.title}
                </MenuItem>
              );
            })}
        </Select>
        <FormHelperText>
          {formik.errors.externalAccess && formik.touched.externalAccess
            ? formik.errors.externalAccess
            : ""}
        </FormHelperText>
      </FormControl>

      {projects.externalAccess === "restricted-link-access" && (
        <div
          className="authorized-users-section"
          style={{ marginBottom: "40px" }}
        >
          <div className="boldTextlabel" style={{ marginBottom: "15px" }}>
            Authorized External Users
          </div>
          <div className="authorized-users-table-container">
            <table className="authorized-users-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email ID</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {projects.authorizedExternalUsers &&
                  projects.authorizedExternalUsers.map((user, idx) => (
                    <tr key={idx}>
                      <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{user.emailId}</td>
                      <td className="DeleteAction-cell">
                        <img
                          src={
                            process.env.PUBLIC_URL + "/assests/img/newTrash.svg"
                          }
                          alt="delete"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleRemoveExternalUser(idx)}
                        />
                      </td>
                    </tr>
                  ))}
                <tr>
                  <td>
                    <div className="input-cell-content">
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="First Name"
                        value={extUser.firstName}
                        onChange={(e) => {
                          setExtUser({ ...extUser, firstName: e.target.value });
                          setExtUserErrors({ ...extUserErrors, firstName: "" });
                        }}
                        error={Boolean(extUserErrors.firstName)}
                        helperText={extUserErrors.firstName}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="input-cell-content">
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Last Name"
                        value={extUser.lastName}
                        onChange={(e) => {
                          setExtUser({ ...extUser, lastName: e.target.value });
                          setExtUserErrors({ ...extUserErrors, lastName: "" });
                        }}
                        error={Boolean(extUserErrors.lastName)}
                        helperText={extUserErrors.lastName}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="input-cell-content">
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Email ID"
                        value={extUser.emailId}
                        onChange={(e) => {
                          setExtUser({ ...extUser, emailId: e.target.value });
                          setExtUserErrors({ ...extUserErrors, emailId: "" });
                        }}
                        error={Boolean(extUserErrors.emailId)}
                        helperText={extUserErrors.emailId}
                      />
                    </div>
                  </td>
                  <td className="DeleteAction-cell">
                    <img
                      src={process.env.PUBLIC_URL + "/assests/img/newTrash.svg"}
                      alt="delete"
                      style={{ cursor: "pointer" }}
                      onClick={handleClearExternalUserInput}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ textAlign: "right", marginTop: "10px" }}>
              <span
                onClick={handleAddExternalUser}
                style={{
                  color: "#333",
                  textDecoration: "underline",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                  fontFamily: "Roboto",
                }}
              >
                Add New Row
              </span>
            </div>
          </div>
        </div>
      )}

      {(projects.externalAccess === "restricted-link-access" ||
        projects.externalAccess === "open-link-access") && (
          <div
            className="hide-contact-info-section"
            style={{
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <span className="boldTextlabel" style={{width: "300px"}}>
              Hide Applicant Contact Information :
            </span>
            <RadioGroup
              row
              name="hideContactInfo"
              value={projects.hideContactInfo ? "yes" : "no"}
              onChange={(e) =>
                onChangeHandler(e.target.value === "yes", "hideContactInfo")
              }
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    size="small"
                    sx={{
                      "&.Mui-checked": {
                        color: "var(--primary-bg-color)",
                      },
                    }}
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    size="small"
                    sx={{
                      "&.Mui-checked": {
                        color: "var(--primary-bg-color)",
                      },
                    }}
                  />
                }
                label="No"
              />
            </RadioGroup>
          </div>
        )}
      {projects.hideContactInfo && (
        <>
          <div
            className="hide-contact-info-section"
            style={{
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <span className="boldTextlabel" style={{width: "300px"}}>Hide Resume :</span>
            <RadioGroup
              row
              name="hideDocuments"
              value={projects.hideDocuments ? "yes" : "no"}
              onChange={(e) =>
                onChangeHandler(e.target.value === "yes", "hideDocuments")
              }
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    size="small"
                    sx={{
                      "&.Mui-checked": {
                        color: "var(--primary-bg-color)",
                      },
                    }}
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    size="small"
                    sx={{
                      "&.Mui-checked": {
                        color: "var(--primary-bg-color)",
                      },
                    }}
                  />
                }
                label="No"
              />
            </RadioGroup>
          </div>
          {projects.hideDocuments === false && (
            <p
              style={{
                textAlign: "left",
                color: "#d32f2f",
              }}
            >
              If the resume contains contact information (such as email or phone
              number), it will remain visible and cannot be automatically
              hidden.
            </p>
          )}
        </>
      )}
      {projects.externalAccess === "restricted-link-access" && (
          <>
            <div
              className="hide-contact-info-section"
              style={{
                marginTop: "40px",
                display: "flex",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <span className="boldTextlabel" style={{width: "300px"}}>
                Enable Notes Section :
              </span>
              <RadioGroup
                row
                name="isNotesVisible"
                value={projects.isNotesVisible ? "yes" : "no"}
                onChange={(e) =>
                  onChangeHandler(e.target.value === "yes", "isNotesVisible")
                }
              >
                <FormControlLabel
                  value="yes"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "var(--primary-bg-color)",
                        },
                      }}
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "var(--primary-bg-color)",
                        },
                      }}
                    />
                  }
                  label="No"
                />
              </RadioGroup>
            </div>
            <div
              className="hide-contact-info-section"
              style={{
                marginTop: "40px",
                display: "flex",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <span className="boldTextlabel" style={{width: "300px"}}>
                Update Status on every submission:
              </span>
              <RadioGroup
                row
                name="isApplicantStatusEnabled"
                value={projects.isApplicantStatusEnabled ? "yes" : "no"}
                onChange={(e) =>
                  onChangeHandler(e.target.value === "yes", "isApplicantStatusEnabled")
                }
              >
                <FormControlLabel
                  value="yes"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "var(--primary-bg-color)",
                        },
                      }}
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "var(--primary-bg-color)",
                        },
                      }}
                    />
                  }
                  label="No"
                />
              </RadioGroup>
            </div>
          </>
        )}
      <div className="projects-footer">
        <Button
          variant="text"
          type="button"
          className="addApplicantcancelBtn"
          size="small"
          onClick={() => onReceiveProps(false)}
        >
          <Link
            href="#"
            style={{
              color: "#ff0000",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            Cancel
          </Link>
        </Button>
        <Button
          type="button"
          variant="contained"
          size="small"
          className="projects-save-btn"
          onClick={formik.handleSubmit}
          disabled={disableSubmitBtn}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ProjectsCreate;

const internalVisibilityArray = [
  { id: 1, name: "private", title: "Private" },
  { id: 2, name: "restricted", title: "Restricted" },
  { id: 3, name: "organization-wide", title: "Organization - wide" },
];

const externalAccessArray = [
  { id: 1, name: "disabled", title: "Disabled" },
  { id: 2, name: "open-link-access", title: "Open Link Access" },
  {
    id: 3,
    name: "restricted-link-access",
    title: "Restricted Link Access (Email Whitelist)",
  },
];
